/* strategy-ledger.jsx — per-strategy performance ledger: overall + week by week.
 *
 * Added 2026-08-17 at operator request, replacing "read the P&L column and cut
 * the losers" with an evidence table.  The columns that matter are not the P&L
 * ones: Sharpe, t, and best-week concentration are what separate a real edge
 * from a lucky fortnight.  On this book NOTHING currently clears |t| > 2, and
 * the table is built to make that visible rather than hide it behind a ranking.
 *
 * Source: window.TRADES (round-trip rows from pairFillsToTrades, /api/fills).
 * Sharpe/t are computed from DAILY aggregated P&L, never per-trade — per-trade
 * Sharpe is meaningless when trade counts differ 10x across strategies.
 *
 * KNOWN DATA LIMITS, surfaced in the coverage banner rather than buried:
 *   - the hub holds aviaf-paper fills only; aviaf-live is absent
 *   - fills.reason is blank fleet-wide, so CLEAN$ (excluding reconcile/
 *     flatten/manual/kill exits) cannot be computed here
 *   - live ledgers are zero-slip signal-price => every figure is an
 *     optimistic upper bound
 *   - broker liquidations booked at $0 understate combine10/11/12
 *
 * Colour uses the dashboard's own --pos/--neg tokens so the ledger matches
 * every other page in every theme; each cell also carries its signed number,
 * so colour is reinforcement and never the only channel.
 */
const { useState: useSL, useMemo: useSLM } = React;

/* Below this many distinct trading days, Sharpe and t are not reported at all.
 * They are ratios of a mean to a standard error, and on 2-3 days they produce
 * spectacular nonsense: mr_v4 shows "Sharpe 4.65" on TWO days, spread_mr_nq_rty
 * "-17.39" on three.  Printing those invites exactly the mistake this table
 * exists to prevent, so they render as "—" with the day count as the reason. */
const SL_MIN_DAYS = 5;

/* ── metrics ─────────────────────────────────────────────────────────── */

function slWeekKey(d) {
  // ISO-ish year-week, matching the hub's other weekly rollups.
  const dt = new Date(d);
  const t = new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate()));
  const dayNum = (t.getUTCDay() + 6) % 7;             // Mon=0
  t.setUTCDate(t.getUTCDate() - dayNum + 3);          // nearest Thursday
  const firstThu = new Date(Date.UTC(t.getUTCFullYear(), 0, 4));
  const fDayNum = (firstThu.getUTCDay() + 6) % 7;
  firstThu.setUTCDate(firstThu.getUTCDate() - fDayNum + 3);
  const wk = 1 + Math.round((t - firstThu) / (7 * 864e5));
  return `${t.getUTCFullYear()}-W${String(wk).padStart(2, "0")}`;
}

function slStats(arr) {
  const n = arr.length;
  if (!n) return { n: 0, mean: 0, sd: 0, t: 0, sharpe: 0 };
  const mean = arr.reduce((a, b) => a + b, 0) / n;
  if (n < 2) return { n, mean, sd: 0, t: 0, sharpe: 0 };
  const sd = Math.sqrt(arr.reduce((a, b) => a + (b - mean) ** 2, 0) / (n - 1));
  return {
    n, mean, sd,
    t: sd > 0 ? mean / (sd / Math.sqrt(n)) : 0,
    sharpe: sd > 0 ? (mean / sd) * Math.sqrt(252) : 0,
  };
}

window.buildStrategyLedger = function buildStrategyLedger(trades) {
  const rows = (trades || []).filter((t) => t.isExit && t.strategy);
  const byStrat = new Map();
  const weekSet = new Set();

  for (const t of rows) {
    const s = t.strategy;
    if (!byStrat.has(s)) {
      byStrat.set(s, {
        id: s, exits: 0, total: 0, wins: 0, contracts: 0,
        symbols: new Set(), accounts: new Set(),
        daily: new Map(), weekly: new Map(),
        first: t.exitTime, last: t.exitTime,
      });
    }
    const o = byStrat.get(s);
    const pnl = Number(t.pnl) || 0;
    const day = String(t.exitTime).slice(0, 10);
    const wk = slWeekKey(t.exitTime);
    weekSet.add(wk);
    o.exits += 1;
    o.total += pnl;
    if (pnl > 0) o.wins += 1;
    o.contracts += Math.abs(Number(t.size) || 0);
    if (t.symbol) o.symbols.add(t.symbol);
    if (t.account) o.accounts.add(t.account);
    o.daily.set(day, (o.daily.get(day) || 0) + pnl);
    o.weekly.set(wk, (o.weekly.get(wk) || 0) + pnl);
    if (t.exitTime < o.first) o.first = t.exitTime;
    if (t.exitTime > o.last) o.last = t.exitTime;
  }

  const weeks = [...weekSet].sort();
  const out = [];
  for (const o of byStrat.values()) {
    const dailyVals = [...o.daily.values()];
    const weeklyVals = [...o.weekly.values()];
    const st = slStats(dailyVals);
    const bestWk = weeklyVals.length ? Math.max(...weeklyVals) : 0;
    out.push({
      id: o.id,
      exits: o.exits,
      total: o.total,
      perExit: o.exits ? o.total / o.exits : 0,
      perContract: o.contracts ? o.total / o.contracts : 0,
      wr: o.exits ? (100 * o.wins) / o.exits : 0,
      days: st.n,
      sharpe: st.sharpe,
      t: st.t,
      sdDay: st.sd,
      weeks: weeklyVals.length,
      posWeeks: weeklyVals.filter((v) => v > 0).length,
      negWeeks: weeklyVals.filter((v) => v < 0).length,
      bestWeek: bestWk,
      // concentration: share of the total that came from the single best week.
      // >100% means everything outside that week is a net loss.
      conc: o.total > 0 ? (100 * bestWk) / o.total : null,
      symbols: [...o.symbols].sort(),
      accounts: [...o.accounts].sort(),
      weekly: o.weekly,
      first: String(o.first).slice(0, 10),
      last: String(o.last).slice(0, 10),
    });
  }
  return { rows: out, weeks };
};

/* ── formatting ──────────────────────────────────────────────────────── */
const slMoney = (v) =>
  (v < 0 ? "-" : "+") + "$" + Math.abs(Math.round(v)).toLocaleString();
const slNum = (v, dp = 2) => (v >= 0 ? "+" : "") + v.toFixed(dp);
const slCls = (v) => (v > 0 ? "pos" : v < 0 ? "neg" : "");

/* ── component ───────────────────────────────────────────────────────── */

/* Selectable window. Applied to the TRADES feed before anything is derived, so
 * the overall table, Sharpe, t and the weekly matrix all describe the SAME
 * period — a period filter that moved only the heatmap would be worse than
 * none, because the Sharpe beside it would silently still be all-time. */
const SL_PERIODS = [
  { id: "4w",  label: "4w",  weeks: 4 },
  { id: "8w",  label: "8w",  weeks: 8 },
  { id: "13w", label: "13w", weeks: 13 },
  { id: "26w", label: "26w", weeks: 26 },
  { id: "all", label: "All", weeks: null },
];

window.StrategyLedger = function StrategyLedger() {
  const [sort, setSort] = useSL("total");
  const [desc, setDesc] = useSL(true);
  const [minExits, setMinExits] = useSL(3);
  const [period, setPeriod] = useSL("all");

  const allTrades = (window.TRADES || []);
  const liveRev = (window.__LIVE && window.__LIVE.rev) || 0;

  const trades = useSLM(() => {
    const p = SL_PERIODS.find((x) => x.id === period);
    if (!p || !p.weeks) return allTrades;
    const cutoff = new Date(Date.now() - p.weeks * 7 * 864e5).toISOString();
    return allTrades.filter((t) => t.exitTime && t.exitTime >= cutoff);
  }, [allTrades.length, liveRev, period]);

  const { rows, weeks } = useSLM(
    () => window.buildStrategyLedger(trades),
    [trades.length, liveRev, period],
  );

  const shown = useSLM(() => {
    const f = rows.filter((r) => r.exits >= minExits);
    const dir = desc ? -1 : 1;
    return f.sort((a, b) => {
      const av = a[sort], bv = b[sort];
      if (typeof av === "string") return dir * String(bv).localeCompare(String(av));
      return dir * ((av ?? 0) - (bv ?? 0));
    });
  }, [rows, sort, desc, minExits]);

  const fleet = rows.reduce((a, r) => a + r.total, 0);
  // Only POSITIVE t matters for "has anything demonstrated an edge".  Counting
  // |t| would report the retired losers (mr_mom t=-2.11 on 2 days) as
  // "significant", which is true and useless.
  const nSig = rows.filter((r) => r.t > 2 && r.days >= SL_MIN_DAYS).length;
  // E[max |t|] across k zero-edge strategies ~ sqrt(2 ln k): the bar any
  // "best performer" has to clear before it means anything at all.
  const expMaxT = Math.sqrt(2 * Math.log(Math.max(rows.length, 2)));
  const bestT = rows.reduce(
    (m, r) => (r.days >= SL_MIN_DAYS && r.t > m ? r.t : m), -Infinity);

  // heat scale: 90th percentile of |weekly move| so one outlier doesn't
  // flatten the whole matrix
  const heatCap = useSLM(() => {
    const vals = [];
    for (const r of rows) for (const v of r.weekly.values()) if (v) vals.push(Math.abs(v));
    if (!vals.length) return 1;
    vals.sort((a, b) => a - b);
    return vals[Math.floor(vals.length * 0.9)] || 1;
  }, [rows]);

  const heatStyle = (v) => {
    if (v == null) return {};
    const a = Math.min(Math.abs(v) / heatCap, 1) * 0.42;
    const tok = v > 0 ? "var(--pos)" : "var(--neg)";
    return { background: `color-mix(in oklab, ${tok} ${(a * 100).toFixed(1)}%, transparent)` };
  };

  const Th = ({ k, children, cls = "num" }) => (
    <th
      className={cls}
      style={{ cursor: "pointer", whiteSpace: "nowrap" }}
      title="Sort"
      onClick={() => { if (sort === k) setDesc(!desc); else { setSort(k); setDesc(true); } }}
    >
      {children}{sort === k ? (desc ? " ▾" : " ▴") : ""}
    </th>
  );

  return (
    <div className="strategy-ledger">
      <window.SectionHeader
        title="Performance ledger"
        count={shown.length}
        sub="Overall and week by week. Sharpe and t are computed from daily P&L; sort by any column."
      />

      {/* ── coverage / honesty banner ── */}
      <div className="sl-coverage">
        <b>Coverage</b>
        <span>hub holds <code>aviaf-paper</code> only — <code>aviaf-live</code> fills absent</span>
        <span><code>reason</code> blank fleet-wide → CLEAN$ not computable; reconcile/flatten exits included</span>
        <span>zero-slip signal-price ledger → figures are an optimistic upper bound</span>
      </div>

      {/* ── the stat that governs how to read the table ── */}
      <div className={"sl-verdict " + (nSig ? "" : "sl-verdict-null")}>
        {nSig === 0 ? (
          <>
            <b>No strategy has demonstrated a positive edge (t &gt; 2).</b> With{" "}
            {rows.length} strategies and zero true edge anywhere, the expected maximum t
            is ~{expMaxT.toFixed(2)}; the best observed is{" "}
            {bestT === -Infinity ? "—" : slNum(bestT)}. Ranking by P&amp;L and cutting the
            losers selects noise — disposition needs a documented prior, not this column.
            Sharpe and t are hidden below {SL_MIN_DAYS} trading days, where they are
            arithmetic noise.
          </>
        ) : (
          <>
            <b>{nSig} strateg{nSig === 1 ? "y has" : "ies have"} t &gt; 2</b> — against an
            expected maximum of ~{expMaxT.toFixed(2)} for {rows.length} zero-edge
            strategies. Check the documented prior before acting on it.
          </>
        )}
      </div>

      <div className="sl-controls">
        <span className="sl-periods" role="group" aria-label="Period">
          {SL_PERIODS.map((p) => (
            <button
              key={p.id}
              type="button"
              data-active={period === p.id}
              onClick={() => setPeriod(p.id)}
              title={p.weeks ? `Last ${p.weeks} weeks` : "Everything in the fills window"}
            >{p.label}</button>
          ))}
        </span>
        <label>
          min exits{" "}
          <input
            type="number" min="1" max="200" value={minExits}
            onChange={(e) => setMinExits(Math.max(1, Number(e.target.value) || 1))}
          />
        </label>
        <span className="micro muted">
          fleet {slMoney(fleet)} · {rows.length} strategies ·{" "}
          {weeks.length ? `${weeks.length} weeks (${weeks[0].replace(/^\d+-/, "")}–${weeks[weeks.length - 1].replace(/^\d+-/, "")})` : "no data"}
          {period !== "all" ? " · period-filtered: every column below describes this window" : ""}
        </span>
      </div>

      {/* ── overall ── */}
      <div className="table-wrap">
        <table className="strat sl-table">
          <thead>
            <tr>
              <Th k="id" cls="">Strategy</Th>
              <Th k="exits">Exits</Th>
              <Th k="total">Total $</Th>
              <Th k="perExit">$/exit</Th>
              <Th k="perContract">$/contract</Th>
              <Th k="wr">WR</Th>
              <Th k="days">Days</Th>
              <Th k="sharpe">Sharpe</Th>
              <Th k="t">t</Th>
              <Th k="conc">Best wk %</Th>
              <Th k="weeks">+/− wks</Th>
              <th>Symbols</th>
              <th>Active</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.id}>
                <td><code>{r.id}</code></td>
                <td className="num">{r.exits}</td>
                <td className={"num " + slCls(r.total)}><b>{slMoney(r.total)}</b></td>
                <td className={"num " + slCls(r.perExit)}>{slNum(r.perExit, 1)}</td>
                <td className={"num " + slCls(r.perContract)}>{slNum(r.perContract, 1)}</td>
                <td className="num">{r.wr.toFixed(0)}%</td>
                <td className={"num " + (r.days < SL_MIN_DAYS ? "sl-thin" : "")}>{r.days}</td>
                <td className={"num " + (r.days < SL_MIN_DAYS ? "muted" : slCls(r.sharpe))}
                    title={r.days < SL_MIN_DAYS
                      ? `only ${r.days} trading day${r.days === 1 ? "" : "s"} — not reportable`
                      : undefined}>
                  {r.days < SL_MIN_DAYS ? "—" : slNum(r.sharpe)}
                </td>
                <td
                  className={"num " + (r.days < SL_MIN_DAYS
                    ? "muted"
                    : (r.t > 2 ? "sl-sig" : (r.t < -2 ? "sl-warn" : "muted")))}
                  title={r.days < SL_MIN_DAYS
                    ? `only ${r.days} trading day${r.days === 1 ? "" : "s"} — not reportable`
                    : (Math.abs(r.t) > 2
                        ? "distinguishable from zero"
                        : "NOT distinguishable from zero")}
                >{r.days < SL_MIN_DAYS ? "—" : slNum(r.t)}</td>
                <td className={"num " + (r.conc != null && r.conc >= 70 ? "sl-warn" : "muted")}
                    title="share of the total that came from the single best week">
                  {r.conc == null ? "—" : r.conc.toFixed(0) + "%"}
                </td>
                <td className="num micro">{r.posWeeks}/{r.negWeeks}</td>
                <td className="micro muted">{r.symbols.join(",")}</td>
                <td className="micro muted">{r.first.slice(5)}→{r.last.slice(5)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── weekly matrix ── */}
      <window.SectionHeader
        title="Week by week"
        sub={`Realised P&L per strategy per ISO week. Colour saturates at ±$${Math.round(heatCap).toLocaleString()} (90th pct); the number is always shown.`}
      />
      <div className="table-wrap">
        <table className="strat sl-table sl-matrix">
          <thead>
            <tr>
              <th className="sl-sticky">Strategy</th>
              {weeks.map((w) => <th key={w} className="num micro">{w.replace(/^\d+-/, "")}</th>)}
              <th className="num">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr className="sl-fleet">
              {/* Wrapped in <code> like every strategy row below it: without
                  this the bare text sits flush against the sticky column's
                  left edge while the rows under it are indented by <code>'s
                  own padding, and the row reads as if it belongs to a
                  different table. */}
              <th className="sl-sticky"><code>FLEET</code></th>
              {weeks.map((w) => {
                const v = rows.reduce((a, r) => a + (r.weekly.get(w) || 0), 0) || null;
                return <td key={w} className="num" style={heatStyle(v)}>
                  {v == null ? "" : slMoney(v)}
                </td>;
              })}
              <td className={"num " + slCls(fleet)}><b>{slMoney(fleet)}</b></td>
            </tr>
            {shown.map((r) => (
              <tr key={r.id}>
                <th className="sl-sticky"><code>{r.id}</code></th>
                {weeks.map((w) => {
                  const v = r.weekly.has(w) ? r.weekly.get(w) : null;
                  return <td key={w} className="num" style={heatStyle(v)}>
                    {v == null ? "" : slMoney(v)}
                  </td>;
                })}
                <td className={"num " + slCls(r.total)}><b>{slMoney(r.total)}</b></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ── scoped styles ───────────────────────────────────────────────────── */
(function injectStrategyLedgerCss() {
  if (document.getElementById("strategy-ledger-css")) return;
  const el = document.createElement("style");
  el.id = "strategy-ledger-css";
  el.textContent = `
.strategy-ledger { margin-top: 18px; }
.strategy-ledger .sl-coverage {
  display: flex; flex-wrap: wrap; gap: 6px 16px; align-items: baseline;
  font-size: 11.5px; color: var(--muted); border: 1px solid var(--line, #2a2e36);
  border-left: 3px solid var(--neg); border-radius: 3px;
  padding: 8px 12px; margin: 10px 0;
}
.strategy-ledger .sl-coverage b { color: var(--neg); font-size: 10.5px;
  letter-spacing: .12em; text-transform: uppercase; }
.strategy-ledger .sl-verdict {
  font-size: 12.5px; line-height: 1.55; color: var(--muted-2, var(--muted));
  border: 1px solid var(--line, #2a2e36); border-left: 3px solid var(--accent);
  border-radius: 3px; padding: 10px 13px; margin: 10px 0 14px;
}
.strategy-ledger .sl-verdict b { color: var(--fg, inherit); }
.strategy-ledger .sl-verdict-null { border-left-color: var(--neg); }
.strategy-ledger .sl-controls {
  display: flex; gap: 16px; align-items: center; margin: 0 0 8px;
  font-size: 12px; color: var(--muted);
}
.strategy-ledger .sl-controls input {
  width: 58px; background: transparent; color: inherit;
  border: 1px solid var(--line, #2a2e36); border-radius: 3px; padding: 2px 6px;
}
.strategy-ledger .sl-periods { display: inline-flex; gap: 2px; }
.strategy-ledger .sl-periods button {
  font-family: ui-monospace, Menlo, monospace; font-size: 11px;
  background: transparent; color: var(--muted); cursor: pointer;
  border: 1px solid var(--line, #2a2e36); border-radius: 3px; padding: 3px 9px;
}
.strategy-ledger .sl-periods button:hover { color: inherit; }
.strategy-ledger .sl-periods button[data-active="true"] {
  color: var(--accent); border-color: var(--accent);
  background: var(--accent-soft, transparent);
}
.strategy-ledger .sl-periods button:focus-visible {
  outline: 2px solid var(--accent); outline-offset: 1px;
}
.strategy-ledger .sl-table td, .strategy-ledger .sl-table th { white-space: nowrap; }
.strategy-ledger .sl-table td.num, .strategy-ledger .sl-table th.num {
  font-variant-numeric: tabular-nums; text-align: right;
}
.strategy-ledger .sl-sig { color: var(--accent); font-weight: 650; }
.strategy-ledger .sl-warn { color: var(--neg); font-weight: 650; }
.strategy-ledger .sl-thin { color: var(--muted); font-style: italic; }
.strategy-ledger .sl-matrix td { font-size: 11px; min-width: 74px; }
.strategy-ledger .sl-matrix .sl-sticky {
  position: sticky; left: 0; z-index: 1;
  background: var(--panel, var(--bg, #14161a));
  text-align: left; font-weight: 500;
  /* Explicit left padding: the sticky column is the reading anchor for the
     whole matrix, and it was sitting flush against the viewport edge. */
  padding-left: 12px;
  border-right: 1px solid var(--line, #2a2e36);
}
.strategy-ledger .sl-matrix tr.sl-fleet td,
.strategy-ledger .sl-matrix tr.sl-fleet th {
  border-bottom: 2px solid var(--line, #2a2e36); font-weight: 650;
}
`;
  document.head.appendChild(el);
})();
